import axios, { type AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { tokenStorage } from './storageService';
import type { AuthResponse } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
// Queue of requests waiting for token refresh
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

// Process the queue after token refresh
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Clear all auth data and redirect to login
const clearAuthAndRedirect = () => {
  tokenStorage.clearAll();
  window.location.href = '/login';
};

// Helper function to serialize metadata fields in request data
const serializeMetadata = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(serializeMetadata);
  }

  // Handle objects
  if (typeof data === 'object') {
    const dataObj = data as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key in dataObj) {
      if (key === 'metadata' && typeof dataObj[key] === 'object' && dataObj[key] !== null) {
        // Serialize metadata object to JSON string
        result[key] = JSON.stringify(dataObj[key]);
      } else {
        // Recursively process nested objects
        result[key] = serializeMetadata(dataObj[key]);
      }
    }
    return result;
  }

  return data;
};

// Request interceptor to add auth token and serialize metadata
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Serialize metadata fields in request data
    if (config.data) {
      config.data = serializeMetadata(config.data);
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper function to deserialize metadata fields in response data
const deserializeMetadata = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(deserializeMetadata);
  }

  // Handle objects
  if (typeof data === 'object') {
    const dataObj = data as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key in dataObj) {
      if (key === 'metadata' && typeof dataObj[key] === 'string') {
        // Parse metadata JSON string to object
        try {
          result[key] = JSON.parse(dataObj[key] as string);
        } catch {
          // If parsing fails, keep as string
          result[key] = dataObj[key];
        }
      } else {
        // Recursively process nested objects
        result[key] = deserializeMetadata(dataObj[key]);
      }
    }
    return result;
  }

  return data;
};

// Response interceptor with automatic token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Deserialize metadata fields in response data (but skip for blob responses)
    if (response.data && !(response.data instanceof Blob)) {
      response.data = deserializeMetadata(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is not 401 or request already retried, reject immediately
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh if this is a refresh-token or auth request
    const url = originalRequest.url || '';
    if (url.includes('/auth/refresh-token') || url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/forgot-password') || url.includes('/auth/reset-password') || url.includes('/auth/logout')) {
      // For auth endpoints, just return the error - let the component handle it
      // Don't redirect because user is already on auth pages or logging out
      return Promise.reject(error);
    }

    // Check if we have both tokens needed for refresh
    const refreshToken = tokenStorage.getRefreshToken();
    const expiredToken = tokenStorage.getToken();
    if (!refreshToken || !expiredToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // Mark as refreshing and retry flag
    isRefreshing = true;
    originalRequest._retry = true;

    try {
      // Try to refresh the token - backend requires both the expired token and refresh token
      const response = await axios.post<AuthResponse>(
        `${API_BASE_URL}/auth/refresh-token`,
        { token: expiredToken, refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { token: newToken, refreshToken: newRefreshToken, expiresAt } = response.data;

      // Save new tokens
      tokenStorage.setTokens(newToken, newRefreshToken, expiresAt);

      // Update the authorization header
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      // Process queued requests with new token
      processQueue(null, newToken);

      // Retry the original request
      return api(originalRequest);
    } catch (refreshError) {
      // Token refresh failed - clear auth and redirect
      processQueue(new Error('Token refresh failed'));
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
